import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import CardActions from '@mui/material/CardActions';
import { useNavigate } from 'react-router-dom';
import { addProductToCart } from '../../../Ecommerce/Cart/CartService';
import { extractErrorMessage } from '../../../../utils/extractErrorMessage';
import useCheckLoginStatus from '../../../../hooks/useCheckLoginStatus'; // Custom hook

const mock = [
  {
    title: 'Personal',
    tagline: 'Explore Docket',
    productId: '1',
    quantity: '1',
    price: '0',
    priceLabel: 'Free forever',
    features: [
      { title: 'Spending tracker', isIncluded: true },
      { title: 'Needs, Wants & Savings categories', isIncluded: true },
      { title: '1 financial goal', isIncluded: true },
      { title: 'iOS & web app', isIncluded: true },
      { title: 'Basic coach insights', isIncluded: true },
      { title: 'Unlimited goals', isIncluded: false },
      { title: 'Household sharing', isIncluded: false },
    ],
    isHighlighted: false,
    btnText: 'Get started free',
  },
  {
    title: 'Plus',
    tagline: 'Your personal Docket',
    productId: '2',
    quantity: '1',
    price: '5.99',
    priceLabel: 'per month',
    features: [
      { title: 'Spending tracker', isIncluded: true },
      { title: 'Needs, Wants & Savings categories', isIncluded: true },
      { title: 'Unlimited goals', isIncluded: true },
      { title: 'iOS & web app', isIncluded: true },
      { title: 'Advanced coach insights', isIncluded: true },
      { title: 'Household sharing', isIncluded: false },
      { title: 'Business workspace', isIncluded: false },
    ],
    isHighlighted: true,
    btnText: 'Add to cart',
  },
  {
    title: 'Family',
    tagline: 'Your shared Docket',
    productId: '5',
    quantity: '1',
    price: '8.99',
    priceLabel: 'per month · 2 adults included',
    features: [
      { title: 'Spending tracker', isIncluded: true },
      { title: 'Needs, Wants & Savings categories', isIncluded: true },
      { title: 'Unlimited goals', isIncluded: true },
      { title: 'iOS & web app', isIncluded: true },
      { title: 'Advanced coach insights', isIncluded: true },
      { title: 'Household sharing (2 adults)', isIncluded: true },
      { title: 'Business workspace', isIncluded: false },
    ],
    isHighlighted: false,
    btnText: 'Add to cart',
  },
  {
    title: 'Business',
    tagline: 'Docket for your people',
    productId: '6',
    quantity: '1',
    price: '5',
    priceLabel: 'per user, per month',
    features: [
      { title: 'Spending tracker', isIncluded: true },
      { title: 'Needs, Wants & Savings categories', isIncluded: true },
      { title: 'Unlimited goals', isIncluded: true },
      { title: 'iOS & web app', isIncluded: true },
      { title: 'Advanced coach insights', isIncluded: true },
      { title: 'Household sharing', isIncluded: true },
      { title: 'Business workspace', isIncluded: true },
    ],
    isHighlighted: false,
    btnText: 'Add to cart',
  },
];

const Pricing = ({
  setAlertMessage,
}: {
  setAlertMessage: (message: string | null) => void;
}): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isLoggedIn = useCheckLoginStatus(); // Custom hook to check login status
  // const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const handleAddToCart = (productId: string, quantity: string) => {
    if (!isLoggedIn) {
      setAlertMessage('Please log in to add items to the shopping cart.');
      // navigate('/login');
      return;
    }

    addProductToCart(productId, quantity)
      .then(() => {
        navigate('/ecommerce/cart');
      })
      .catch((error) => {
        console.error('Error adding product to cart:', error);
        setAlertMessage(
          extractErrorMessage(
            error,
            'Failed to add product to cart. Please try again.',
          ),
        );
      });
  };

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ fontWeight: 700 }}
          variant="h4"
          align="center"
          gutterBottom
        >
          Simple, transparent pricing
        </Typography>
        <Typography
          variant="h6"
          component="p"
          color="text.secondary"
          align="center"
        >
          Start free. Upgrade when you need more.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {mock.map((item, i) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={i}
            data-aos="fade-up"
            data-aos-delay={i * 100}
            data-aos-offset={100}
            data-aos-duration={600}
          >
            <Box
              component={Card}
              height={1}
              display="flex"
              flexDirection="column"
              boxShadow={0}
              border={`1px solid ${theme.palette.divider}`}
            >
              <CardContent sx={{ padding: { sm: 4 } }}>
                <Box
                  marginBottom={4}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Typography variant="h6" gutterBottom>
                    <Box component="span" fontWeight={600}>
                      {item.title}
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    {item.tagline}
                  </Typography>
                  <Box display="flex" alignItems="flex-start" marginTop={1}>
                    {item.price !== '0' && (
                      <Typography variant="h4" color="primary">
                        <Box component="span" fontWeight={600} marginRight={0.5}>
                          £
                        </Box>
                      </Typography>
                    )}
                    <Typography variant="h2" color="primary" gutterBottom>
                      <Box component="span" fontWeight={600}>
                        {item.price === '0' ? 'Free' : item.price}
                      </Box>
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" align="center">
                    {item.priceLabel}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {item.features.map((feature, j) => (
                    <Grid item xs={12} key={j}>
                      <Typography
                        component="p"
                        align="center"
                        style={{
                          textDecoration: !feature.isIncluded
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
              <Box flexGrow={1} />
              <CardActions sx={{ justifyContent: 'flex-end', padding: 4 }}>
                <Button
                  size="large"
                  variant={item.isHighlighted ? 'contained' : 'outlined'}
                  onClick={() => handleAddToCart(item.productId, item.quantity)}
                >
                  {item.btnText}
                </Button>
              </CardActions>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Pricing;
